import _ from 'lodash';
import { defineStore } from 'pinia';
import { computed, set, del } from 'vue-demi';

import { Contact, ContactBookSettings } from '@/types';
import { SchemaItem } from './types';
import { useRouter } from '@/composables';
import { SearchContacts } from './search-contacts';

export const useContactsStore = defineStore('HITL-Contacts', {
  state: () => {
    return {
      contactsById: {} as { [contactId: string]: Contact; },
      eventTimestampsById: {} as { [contactId: string]: string; },
      schema: [] as SchemaItem[],
      isContactLoadingById: {} as { [contactId: string]: boolean; },
      /** Contact book settings are provided by default migration and can be manually changed in the database */
      contactBookInfo: {} as ContactBookSettings,
      searchText: '',
    };
  },
  getters: {
    contacts: state => Object.values(state.contactsById),
    getContactById: state => (contactId: string) => {
      return state.contactsById[contactId] as Contact | undefined;
    },
    getContactByPhoneNumber(): (number: string) => Contact | undefined {
      return (number: string) => {
        return this.contacts.find(contact => {
          return contact.phoneNumbers.find(item => item === number);
        });
      };
    },
    isContactLoading: state => (contactId: string) => {
      return state.isContactLoadingById[contactId];
    },
    /** Contacts are sorted in descending order */
    contactsOrderByEventTimestamp: state => {
      const contactIds = Object.keys(state.contactsById);
      contactIds.sort((a, b) => {
        const aTimestamp = new Date(state.eventTimestampsById[a] || 0);
        const bTimestamp = new Date(state.eventTimestampsById[b] || 0);
        return bTimestamp.getTime() - aTimestamp.getTime();
      });

      return contactIds.map(id => state.contactsById[id]);
    },
    contactIdsBySearchText(state): string[] {
      return SearchContacts.searchContactsByText({
        contacts: this.contacts,
        searchText: state.searchText,
      }).map(contact => contact.contactId);
    },
  },
});

export const useContactsGetters = () => {
  const { query, AppRouter } = useRouter();
  const store = useContactsStore();

  const activeContactId = computed(() => {
    return query.value[AppRouter.QUERY_PARAMS.CONTACT_ID] as string;
  });

  const activeContact = computed(() => {
    return store.contactsById[activeContactId.value] as Contact | undefined;
  });

  return { activeContact, activeContactId };
};

export const useContactsSetters = () => {
  const store = useContactsStore();

  const setContactBookInfo = (info: ContactBookSettings) => {
    store.contactBookInfo = info;
  };

  const addOrUpdateContactsInStore = (options: Contact | Contact[]) => {
    const contacts = options instanceof Array ? options : [options];
    const update = _.keyBy(contacts, 'contactId');

    SearchContacts.clearCacheByContactIds(contacts.map(c => c.contactId));
    store.contactsById = { ... store.contactsById, ...update };
  };

  const addOrMergeContactInStore = (contact: Partial<Contact>) => {
    const { contactId } = contact as Contact;
    const current = store.contactsById[contactId];
    const update = current ? { ...current, ...contact } : contact;

    SearchContacts.clearCacheByContactIds([contactId]);
    set(store.contactsById, contact.contactId, update);
  };

  const deleteContactFromStore = (contactId: string) => {
    if (store.contactsById[contactId]) {
      SearchContacts.clearCacheByContactIds([contactId]);
      del(store.contactsById, contactId);
    }
  };

  const updateContactEventTimestamp = (args: {
    contactId: string;
    timestamp?: number | string | Date;
  }) => {
    const incoming = new Date(args.timestamp || 0);
    const current = new Date(store.eventTimestampsById[args.contactId] || 0);

    if (current.getTime() <= incoming.getTime()) {
      set(store.eventTimestampsById, args.contactId, incoming.toUTCString());
    }
  };

  /** Allow setting loading status in bulk */
  const setIsLoadingByContactIds = (args: {
    contactIds: string[];
    isLoading: boolean;
  }) => {
    const update = _.mapValues(_.keyBy(args.contactIds), () => args.isLoading);
    store.isContactLoadingById = { ...store.isContactLoadingById, ...update };
  };

  const setSchema = (args: { schema: SchemaItem[]; }) => {
    const result = args.schema.filter(schemaItem => schemaItem.visibility);
    store.schema = _.sortBy(result, 'order');
  };

  return {
    setContactBookInfo,
    addOrUpdateContactsInStore,
    addOrMergeContactInStore,
    deleteContactFromStore,
    updateContactEventTimestamp,
    setIsLoadingByContactIds,
    setSchema,
  };
};
